import * as Sentry from "@sentry/react";
import Iframe, { iframeLoader } from "components/iframe";
import client from "libs/client";
import queryClient from "libs/queryClient";
import ForgottenPasswordPage from "pages/ForgottenPasswordPage";
import { GamePage } from "pages/GamePage";
import { PlayerUserDetailPage } from "pages/PlayerUserDetailPage";
import { PlayerUserLayoutPage } from "pages/PlayerUserLayoutPage";
import { PlayerUsersPage } from "pages/PlayerUsersPage";
import RedirectPage, { loader as redirectLoader } from "pages/RedirectPage";
import ResetPasswordPage from "pages/ResetPasswordPage";
import { SignupRequestsPage } from "pages/SignupRequestsPage";
import TeamsPage from "pages/TeamsPage";
import ClubsIFramePage from "pages/clubsIframe";
import NotFoundPage from "pages/errorPages/NotFoundPage";
import ErrorPage from "pages/errorPages/errorPage";
import LoginPage from "pages/login";
import { ProductManagement } from "pages/productManagement/Index";
import { RootPage, loader as rootLoader } from "pages/root";
import { UserPage } from "pages/userPage";
import { VideoUploadMetadataPage } from "pages/videoUploads/VideoUploadMetadataPage";
import { VideoUploadStatusPage } from "pages/videoUploads/VideoUploadStatusPage";
import { VideoUploadsAllPage } from "pages/videoUploads/VideoUploadsAllPage";
import { VideoUploadsDeletedPage } from "pages/videoUploads/VideoUploadsDeletedPage";
import { VideoUploadsErroredPage } from "pages/videoUploads/VideoUploadsErroredPage";
import { VideoUploadsFinishedPage } from "pages/videoUploads/VideoUploadsFinishedPage";
import { VideoUploadsInProcessingPage } from "pages/videoUploads/VideoUploadsInProcessingPage";
import { VideoUploadsInQueuePage } from "pages/videoUploads/VideoUploadsInQueuePage";
import { VideoUploadsLayoutPage } from "pages/videoUploads/VideoUploadsLayoutPage";
import { VideoUploadsNotSentPage } from "pages/videoUploads/VideoUploadsNotSentPage";
import { VideoUploadsServerStatusPage } from "pages/videoUploads/VideoUploadsServerStatusPage";
import VideoUploadsTestPage from "pages/videoUploads/VideoUploadsTestPage";
import { Navigate, RouterProvider, createBrowserRouter, useLocation } from "react-router";
import { DiscoverUsersPage } from "pages/DiscoverUsersPage";
import { DiscoverUserPage } from "pages/DiscoverUserPage";
import { DiscoverLayoutPage } from "pages/DiscoverLayoutPage";
import { DiscoverTeamPage } from "pages/DiscoverTeamPage";
import { DiscoverClubHousePage } from "pages/DiscoverClubHousePage";
import { DiscoverSignUpsPage } from "pages/DiscoverSignUpsPage";
import { DiscoverTokenMovementsPage } from "pages/DiscoverTokenMovementsPage";
import { DiscoverClubHousesPage } from "pages/DiscoverClubHousesPage";
import { DiscoverTeamsPage } from "pages/DiscoverTeamsPage";
import { DiscoverSignUpPage } from "pages/DiscoverSignUpPage";
import { DiscoverGamesPage } from "pages/DiscoverGamesPage";
import { DiscoverGamesPageV3 } from "pages/DiscoverGamesPageV3";
import { DiscoverGamePage } from "pages/DiscoverGamePage";
import { DiscoverGamePageV2 } from "pages/DiscoverGamePageV2";
import { OperationsManagementLayoutPage } from "pages/OperationsManagementLayoutPage";
import { DiscoverPlayerDataPage } from "pages/DiscoverPlayerDataPage";
import { DiscoverPlayersDataPage } from "pages/DiscoverPlayersDataPage";
import { OperationsManagementSummaryPage } from "pages/OperationsManagementSummaryPage";
import { SubscriptionListPage } from "pages/SubscriptionListPage";
import { SubscriptionEditPage } from "pages/SubscriptionEditPage";
import { GameEventsDebugPage } from "pages/GameEventsDebugPage";
import { GameStatsDebugPage } from "pages/GameStatsDebugPage";
import { TeamGroupListPage } from "pages/TeamGroupListPage";
import { TeamGroupEditPage } from "pages/TeamGroupEditPage";
import { ProductManagementSubscriptionsLayoutPage } from "pages/ProductManagementSubscriptionsLayoutPage";

function IFramePage() {
	const location = useLocation();
	return <Iframe url={location.search ? `${location.pathname}${location.search}` : location.pathname} />;
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
	//REDIRECTS
	{
		path: "/admin/club-amateur/:id/teams",
		loader: redirectLoader,
		element: <RedirectPage />,
	},
	{
		path: "/admin/club-amateur/:id/match/:id",
		loader: redirectLoader,
		element: <RedirectPage />,
	},
	{
		path: "/admin/videos/match/:id",
		loader: redirectLoader,
		element: <RedirectPage />,
	},
	{
		path: "/admin/club-amateur-nonpartner/:cid/match/:mid",
		loader: redirectLoader,
		element: <RedirectPage />,
	},
	{
		path: "/",
		element: <RootPage />,
		loader: rootLoader(queryClient, client),
		errorElement: <ErrorPage />,
		children: [
			{
				index: true,
				loader: iframeLoader,
				element: <ClubsIFramePage />,
			},
			{
				path: "/product-management",
				element: <ProductManagement />,
			},
			{
				path: "/video-test",
				element: <VideoUploadsTestPage />,
			},
			{
				path: "/admin/clubs-amateur",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/videos",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/clubs-pro",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-amateur/:id",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/clubs-amateur/add",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-amateur/:id/users",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-amateur/:id/users/:id/new-password",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/club/:id/teams",
				element: <TeamsPage />,
			},
			{
				path: "/game/:id",
				element: <GamePage />,
			},
			{
				path: "/game-events-debug/:gameId",
				element: <GameEventsDebugPage />,
			},
			{
				path: "/game-stats-debug/:gameId",
				element: <GameStatsDebugPage />,
			},
			{
				path: "/admin/club-amateur/:id/planification-match",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-amateur-nonpartner/:id",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-amateur-nonpartner/:id/games",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-pro/:id",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-pro/:id/users",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/club-pro/:id/users/:id/new-password",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/admin/clubs-pro/add",
				loader: iframeLoader,
				element: <IFramePage />,
			},
			{
				path: "/users/:id",
				element: <UserPage />,
			},
			{
				path: "/video-uploads/:id/video",
				element: <VideoUploadMetadataPage />,
			},
			{
				path: "/video-uploads/:id/status",
				element: <VideoUploadStatusPage />,
			},
			{
				element: <VideoUploadsLayoutPage />,
				children: [
					{
						path: "/video-uploads/approval",
						element: <Navigate to="/video-uploads/not-sent" replace />,
					},
					{
						path: "/video-uploads/not-sent",
						element: <VideoUploadsNotSentPage />,
					},
					{
						path: "/video-uploads/in-queue",
						element: <VideoUploadsInQueuePage />,
					},
					{
						path: "/video-uploads/in-processing",
						element: <VideoUploadsInProcessingPage />,
					},
					{
						path: "/video-uploads/finished",
						element: <VideoUploadsFinishedPage />,
					},
					{
						path: "/video-uploads/errored",
						element: <VideoUploadsErroredPage />,
					},
					{
						path: "/video-uploads/all",
						element: <VideoUploadsAllPage />,
					},
					{
						path: "/video-uploads/deleted",
						element: <VideoUploadsDeletedPage />,
					},
					{
						path: "/video-uploads/status",
						element: <VideoUploadsServerStatusPage />,
					},
					// {
					// 	path: "/video-uploads/monitoring",
					// 	element: <VideoUploadsMonitoringPage />,
					// },
				],
			},
			{
				element: <PlayerUserLayoutPage />,
				children: [
					{
						path: "/player-users/signup-requests",
						element: <SignupRequestsPage />,
					},
					{
						path: "/player-users/player-user-accounts",
						element: <PlayerUsersPage />,
					},
					{
						path: "/player-users/user/:id",
						element: <PlayerUserDetailPage />,
					},
				],
			},
			{
				element: <DiscoverLayoutPage />,
				children: [
					{
						path: "/discover/sign-up",
						element: <DiscoverSignUpsPage />,
					},
					{
						path: "/discover/sign-up/:id",
						element: <DiscoverSignUpPage />,
					},
					{
						path: "/discover/token-movements",
						element: <DiscoverTokenMovementsPage />,
					},
					{
						path: "/discover/user",
						element: <DiscoverUsersPage />,
					},
					{
						path: "/discover/user/:id",
						element: <DiscoverUserPage />,
					},
					{
						path: "/discover/player-data",
						element: <DiscoverPlayersDataPage />,
					},
					{
						path: "/discover/player-data/:id",
						element: <DiscoverPlayerDataPage />,
					},
					{
						path: "/discover/team",
						element: <DiscoverTeamsPage />,
					},
					{
						path: "/discover/team/:id",
						element: <DiscoverTeamPage />,
					},
					{
						path: "/discover/club",
						element: <DiscoverClubHousesPage />,
					},
					{
						path: "/discover/club/:id",
						element: <DiscoverClubHousePage />,
					},
					{
						path: "/discover/game",
						element: <DiscoverGamesPage />,
					},
					{
						path: "/discover/v2/games",
						element: <DiscoverGamesPageV3 />,
					},
					{
						path: "/discover/game/:id",
						element: <DiscoverGamePage />,
					},
					{
						path: "/discover/v2/game/:id",
						element: <DiscoverGamePageV2 />,
					},
				],
			},
			{
				element: <ProductManagementSubscriptionsLayoutPage />,
				children: [
					{
						path: "/subscription",
						element: <SubscriptionListPage />,
					},
					{
						path: "/subscription/new",
						element: <SubscriptionEditPage />,
					},
					{
						path: "/subscription/:id",
						element: <SubscriptionEditPage />,
					},
					{
						path: "/team-group",
						element: <TeamGroupListPage />,
					},
					{
						path: "/team-group/new",
						element: <TeamGroupEditPage />,
					},
					{
						path: "/team-group/:id",
						element: <TeamGroupEditPage />,
					},
				],
			},
			{
				element: <OperationsManagementLayoutPage />,
				children: [
					{
						path: "/operations-management/summary",
						element: <OperationsManagementSummaryPage />,
					},
					{
						path: "/operations-management/hardware-tracking",
						element: <div>Operations management HARDWARE TRACKING</div>,
					},
					{
						path: "/operations-management/tagging-reporting",
						element: <div>Operations management TAGGING REPORTING</div>,
					},
					{
						path: "/operations-management/tournaments",
						element: <div>Operations management TOURNAMENTS</div>,
					},
					{
						path: "/operations-management/subscription-reporting",
						element: <div>Operations management SUBSCRIPTION REPORTING</div>,
					},
				],
			},
		],
	},
	{
		path: "/forgotten-password",
		element: <ForgottenPasswordPage />,
	},
	{
		path: "/new-password/:token",
		element: <ResetPasswordPage />,
	},
	{
		path: "/login",
		element: <LoginPage />,
	},
	{
		path: "/admin",
		element: <LoginPage />,
	},
	{
		path: "*",
		element: <NotFoundPage />,
	},
]);

export function navigate(dest: string) {
	router.navigate(dest);
}

export default () => <RouterProvider router={router} />;
